import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Cpu, Brain, Bell, MessageCircle, ShieldAlert, Radio, Settings as SettingsIcon,
  LogOut, Search, Activity, Zap, HardDrive, Sparkles, Home, Mail, ScrollText, Trash2, ShieldCheck, Copy, Send, Mic, Play, Pause, Check, UserPlus, TrendingUp, Image as ImageIcon, AlertCircle, MapPin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, ResponsiveContainer, XAxis, Tooltip } from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import RobotFace from "@/components/RobotFace";

const NAV = [
  { id: "overview", icon: <Home size={16}/>, label:"Overview" },
  { id: "users", icon: <Users size={16}/>, label:"Users" },
  { id: "signins", icon: <MapPin size={16}/>, label:"Sign-ins" },
  { id: "invites", icon: <Mail size={16}/>, label:"Admin invites" },
  { id: "devices", icon: <Cpu size={16}/>, label:"Devices" },
  { id: "memory", icon: <Brain size={16}/>, label:"Memory DB" },
  { id: "reminders", icon: <Bell size={16}/>, label:"Reminders" },
  { id: "conversations", icon: <MessageCircle size={16}/>, label:"Conversations" },
  { id: "ai", icon: <Sparkles size={16}/>, label:"AI config" },
  { id: "third-party", icon: <ImageIcon size={16}/>, label:"Third-party usage" },
  { id: "homepage-voice", icon: <Mic size={16}/>, label:"Homepage voice" },
  { id: "campaigns", icon: <Send size={16}/>, label:"Email campaigns" },
  { id: "ota", icon: <Radio size={16}/>, label:"OTA" },
  { id: "audit", icon: <ScrollText size={16}/>, label:"Audit log" },
  { id: "errors", icon: <ShieldAlert size={16}/>, label:"Errors" },
  { id: "security", icon: <SettingsIcon size={16}/>, label:"Security" },
];

export default function Admin() {
  const [screen, setScreen] = useState("overview");
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--rammy-bg)" }}>
      <aside className="w-64 shrink-0 border-r border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/50 p-5 flex flex-col">
        <Link to="/" className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 rounded-xl bg-[var(--rm-ink-900)] flex items-center justify-center">
            <div className="w-2.5 h-2.5 rounded-full bg-[var(--rammy-cyan)] shadow-[0_0_10px_var(--rammy-cyan)]"/>
          </div>
          <span className="font-display text-lg">Rammy · Admin</span>
        </Link>
        <nav className="space-y-1 flex-1">
          {NAV.map(n => (
            <button key={n.id} onClick={()=>setScreen(n.id)} data-testid={`admin-nav-${n.id}`}
              className={`w-full text-left rounded-xl px-3 py-2 flex items-center gap-2.5 text-sm transition ${
                screen===n.id
                  ? "bg-[var(--rm-surface)] border border-[var(--rammy-border)] font-medium rammy-shadow"
                  : "text-[var(--rammy-text-sec)] hover:bg-[var(--rm-surface)]/60"
              }`}
            >{n.icon}{n.label}</button>
          ))}
        </nav>
        <div className="pt-4 border-t border-[var(--rammy-border)]">
          <div className="text-xs text-[var(--rammy-text-muted)]">Signed in</div>
          <div className="text-sm font-medium truncate">{user?.email}</div>
          <Button variant="ghost" onClick={async()=>{await logout();nav("/");}} className="btn-pill w-full mt-2 text-sm" data-testid="admin-logout">
            <LogOut size={14} className="mr-1.5"/>Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--rammy-cyan-dark)] font-semibold">Console</div>
            <h1 className="text-3xl font-display capitalize">{NAV.find(n=>n.id===screen)?.label}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]"/>
              <Input placeholder="Search…" className="pl-9 rounded-full h-10 w-72 bg-[var(--rm-surface)]" data-testid="admin-search"/>
            </div>
            <Link to="/app"><Button className="btn-pill btn-dark h-10 px-4" data-testid="admin-open-app">Open web app</Button></Link>
          </div>
        </div>

        {screen === "overview" && <Overview/>}
        {screen === "users" && <UsersView/>}
        {screen === "signins" && <SigninsView/>}
        {screen === "invites" && <InvitesView/>}
        {screen === "devices" && <DevicesView/>}
        {screen === "memory" && <MemoryView/>}
        {screen === "reminders" && <RemindersView/>}
        {screen === "conversations" && <ConversationsView/>}
        {screen === "ai" && <AiConfigView/>}
        {screen === "third-party" && <ThirdPartyUsageView/>}
        {screen === "homepage-voice" && <HomepageVoiceView/>}
        {screen === "ota" && <OtaView/>}
        {screen === "audit" && <AuditView/>}
        {screen === "campaigns" && <CampaignsView/>}
        {screen === "errors" && <ErrorsView/>}
        {screen === "security" && <SecurityView/>}
      </main>
    </div>
  );
}

/* --------------------------- Sub-views --------------------------- */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading…</div>;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="Users" value={stats.users} icon={<Users size={16}/>}/>
        <Stat label="Devices" value={stats.devices} icon={<Cpu size={16}/>}/>
        <Stat label="Memories" value={stats.memories} icon={<Brain size={16}/>}/>
        <Stat label="Conversations" value={stats.conversations} icon={<MessageCircle size={16}/>}/>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 rammy-shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Messages · last 7 days</div>
              <div className="text-xs text-[var(--rammy-text-muted)]">Total across the fleet</div>
            </div>
            <Badge className="rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border-0">Live</Badge>
          </div>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.trend}>
                <XAxis dataKey="day" hide/>
                <Tooltip cursor={{stroke:"var(--rammy-border)"}} contentStyle={{borderRadius:16, border:"1px solid var(--rammy-border)"}}/>
                <Line type="monotone" dataKey="messages" stroke="#178677" strokeWidth={2.5} dot={{ r: 3, fill: "#178677" }} activeDot={{r:5}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-6 rammy-shadow relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[var(--rammy-cyan)]/25 blur-2xl"/>
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-white/60">Fleet health</div>
            <div className="text-4xl font-display mt-2">99.8%</div>
            <div className="text-white/70 text-sm mt-1">7-day online uptime</div>
            <div className="mt-6">
              <RobotFace expression="happy" size={140} showHud={false}/>
            </div>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Total messages" value={stats.messages.toLocaleString()} icon={<Activity size={16}/>}/>
        <Stat label="Active reminders" value={stats.reminders} icon={<Bell size={16}/>}/>
        <Stat label="Storage" value={"3.4 GB"} icon={<HardDrive size={16}/>}/>
      </div>
    </div>
  );
}
function Stat({ label, value, icon }) {
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5 rammy-shadow">
      <div className="flex items-center justify-between text-[var(--rammy-text-muted)]">
        <span className="text-xs uppercase tracking-widest">{label}</span>
        <span>{icon}</span>
      </div>
      <div className="mt-3 text-3xl font-display">{value}</div>
    </div>
  );
}

/* ---------- Third-party usage hub (Flux + future providers) ------ */
function ThirdPartyUsageView() {
  const [providers, setProviders] = useState(null);
  const [selected, setSelected] = useState("flux");
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/admin/third-party/providers")
      .then(r => setProviders(r.data.providers || []))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load providers"));
  }, []);
  if (err) return <div className="text-sm text-red-600" data-testid="admin-third-party-error">{err}</div>;
  if (!providers) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-third-party">
      {/* Provider selector — click a card to drill in. Live ones are
          interactive, "coming soon" ones are visually greyed out. */}
      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-3">
        {providers.map(p => {
          const isLive = p.status === "live";
          const isSelected = isLive && selected === p.key;
          return (
            <button
              key={p.key}
              type="button"
              disabled={!isLive}
              onClick={() => isLive && setSelected(p.key)}
              className={`text-left rounded-2xl border p-4 transition ${
                isSelected
                  ? "border-[var(--rammy-cyan-dark)] bg-[var(--rm-surface)] rammy-shadow"
                  : isLive
                    ? "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan-dark)]/50"
                    : "border-dashed border-[var(--rammy-border)] bg-[var(--rammy-surface-alt)]/40 cursor-not-allowed opacity-70"
              }`}
              data-testid={`admin-third-party-provider-${p.key}`}
            >
              <div className="flex items-start justify-between">
                <div className="text-[13px] font-medium">{p.name}</div>
                <Badge className={`rounded-full border-0 text-[10px] uppercase tracking-widest ${
                  isLive ? "bg-emerald-100 text-emerald-800" : "bg-[var(--rammy-border)] text-[var(--rammy-text-muted)]"
                }`}>{isLive ? "Live" : "Soon"}</Badge>
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-widest text-[var(--rammy-text-muted)]">{p.kind}</div>
              <div className="mt-2 text-xs text-[var(--rammy-text-muted)] line-clamp-2">{p.note}</div>
            </button>
          );
        })}
      </div>

      {selected === "flux" && <FluxProviderDetail/>}
      {selected === "tavily" && <TavilyProviderDetail/>}
    </div>
  );
}


function TavilyProviderDetail() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/third-party/tavily/usage")
      .then(r => { if (alive) { setData(r.data); setErr(null); } })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Failed to load"); });
    load();
    const id = setInterval(load, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (err) return <div className="text-sm text-red-600" data-testid="admin-tavily-error">{err}</div>;
  if (!data) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading Tavily…</div>;

  const fmtMoney = (v) => `$${(v || 0).toFixed(3)}`;
  const buckets = [
    { key: "this_month", label: "This month", d: data.this_month },
    { key: "last_30d",   label: "Last 30 days", d: data.last_30d },
    { key: "all_time",   label: "All time",     d: data.all_time },
  ];

  return (
    <div className="space-y-6" data-testid="admin-tavily-detail">
      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {buckets.map(({ key, label, d }) => (
          <div
            key={key}
            className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-4"
            data-testid={`admin-tavily-bucket-${key}`}
          >
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">{label}</div>
            <div className="text-3xl font-display mt-1" data-testid={`admin-tavily-${key}-cost`}>
              {fmtMoney(d?.est_cost_usd)}
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-[var(--rammy-text-sec)]">
              <span data-testid={`admin-tavily-${key}-basic`}><b>{d?.basic_ok || 0}</b> basic</span>
              <span data-testid={`admin-tavily-${key}-adv`}><b>{d?.advanced_ok || 0}</b> advanced</span>
              <span className="text-[var(--rammy-text-muted)]">·</span>
              <span>{d?.credits || 0} credits</span>
              {d?.fail > 0 && (
                <span className="text-red-500">· {d.fail} failed</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Per-user this-month */}
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-tavily-by-user">
        <div className="px-4 py-3 border-b border-[var(--rammy-border)] flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">By user · this month</div>
            <div className="text-[11px] text-[var(--rammy-text-muted)]">
              Sorted by basic → advanced count. Basic ${data.unit_cost_usd?.basic}/call, Advanced ${data.unit_cost_usd?.advanced}/call.
            </div>
          </div>
          <div className="text-[11px] font-mono text-[var(--rammy-text-muted)]" data-testid="admin-tavily-user-count">
            {data.by_user?.length || 0} users
          </div>
        </div>
        <div className="max-h-[360px] overflow-y-auto subtle-scroll">
          {(!data.by_user || data.by_user.length === 0) ? (
            <div className="p-6 text-sm text-[var(--rammy-text-muted)] text-center">
              No Tavily searches this month yet.
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-tavily-user-table">
              <thead className="bg-[var(--rammy-surface-alt)]/50 text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">User</th>
                  <th className="text-right px-3 py-2 font-semibold">Basic</th>
                  <th className="text-right px-3 py-2 font-semibold">Advanced</th>
                  <th className="text-right px-3 py-2 font-semibold">Credits</th>
                  <th className="text-right px-3 py-2 font-semibold">Cost</th>
                  <th className="text-right px-4 py-2 font-semibold">Last</th>
                </tr>
              </thead>
              <tbody>
                {data.by_user.map((u, i) => (
                  <tr
                    key={u.email || i}
                    className="border-t border-[var(--rammy-border)] hover:bg-[var(--rammy-surface-alt)]/30"
                    data-testid={`admin-tavily-user-row-${i}`}
                  >
                    <td className="px-4 py-2 font-mono text-xs truncate max-w-[280px]">{u.email}</td>
                    <td className="px-3 py-2 text-right font-mono">{u.basic_ok}</td>
                    <td className="px-3 py-2 text-right font-mono">{u.advanced_ok}</td>
                    <td className="px-3 py-2 text-right font-mono">{u.credits}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-[var(--rammy-cyan-dark)]">{fmtMoney(u.est_cost_usd)}</td>
                    <td className="px-4 py-2 text-right text-[11px] text-[var(--rammy-text-muted)]">
                      {u.last_at ? new Date(u.last_at).toLocaleString(undefined, { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recent audit rows */}
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-tavily-recent">
        <div className="px-4 py-3 border-b border-[var(--rammy-border)] text-sm font-medium">Recent searches</div>
        <div className="divide-y divide-[var(--rammy-border)]">
          {(!data.recent || data.recent.length === 0) ? (
            <div className="p-6 text-sm text-[var(--rammy-text-muted)] text-center">Nothing yet.</div>
          ) : (
            data.recent.map((r, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center gap-3 text-[13px]" data-testid={`admin-tavily-recent-${i}`}>
                <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${
                  r.depth === "advanced"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-blue-100 text-blue-700"
                }`}>{r.depth}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${r.ok ? "bg-emerald-500" : "bg-red-500"}`}/>
                <span className="font-mono text-xs text-[var(--rammy-text-muted)] shrink-0">{r.email || "—"}</span>
                <span className="flex-1 truncate">{r.query || "—"}</span>
                <span className="text-[11px] text-[var(--rammy-text-muted)] shrink-0">
                  {r.created_at ? new Date(r.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function FluxProviderDetail() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/admin/third-party/flux/usage")
      .then(r => { if (alive) { setData(r.data); setLastUpdated(new Date()); setErr(null); } })
      .catch(e => { if (alive) setErr(e?.response?.data?.detail || "Failed to load"); });
    load();
    // Poll every 6s so new Flux events surface in near-real-time — cheap
    // (one Mongo aggregate per tick, admin-only route).
    const id = setInterval(load, 6000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (err) return <div className="text-sm text-red-600" data-testid="admin-flux-error">{err}</div>;
  if (!data) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading Flux…</div>;

  const t = data.this_month;
  const successRate = t.total > 0 ? Math.round((t.ok / t.total) * 100) : 0;
  // Only show the "credits exhausted" banner when the MOST RECENT event was
  // a Fal billing failure. Older failures are historical — no need to alarm
  // an admin whose account is clearly live again.
  const mostRecent = (data.recent || [])[0];
  const outOfCreditsHit = mostRecent && !mostRecent.ok &&
    /exhausted balance|user is locked/i.test(mostRecent.error || "");

  return (
    <div className="space-y-6" data-testid="admin-flux-detail">
      {outOfCreditsHit && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3" data-testid="admin-flux-credits-warning">
          <AlertCircle size={18} className="text-amber-700 mt-0.5"/>
          <div className="flex-1 text-sm">
            <div className="font-medium text-amber-900">Fal.ai credits exhausted</div>
            <div className="text-amber-800 mt-0.5">
              The Flux fallback is currently blocked. Top up at{" "}
              <a href="https://fal.ai/dashboard/billing" target="_blank" rel="noreferrer" className="underline font-medium">fal.ai/dashboard/billing</a>{" "}
              to restore image generation for prompts OpenAI refuses.
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <Stat label="This month" value={t.total} icon={<ImageIcon size={16}/>}/>
        <Stat label="Successful" value={t.ok} icon={<Check size={16}/>}/>
        <Stat label="Failed" value={t.fail} icon={<AlertCircle size={16}/>}/>
        <Stat label="Est. spend" value={`$${t.est_cost_usd.toFixed(2)}`} icon={<TrendingUp size={16}/>}/>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 rammy-shadow">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Flux generations · last 30 days</div>
              <div className="text-xs text-[var(--rammy-text-muted)]">
                Model: <span className="font-mono">{data.model}</span> · ${data.unit_cost_usd}/image
              </div>
            </div>
            <Badge
              className="rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border-0 inline-flex items-center gap-1.5"
              data-testid="admin-flux-live-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan-dark)] animate-pulse"/>
              Live
              {lastUpdated && (
                <span className="text-[10px] opacity-70 font-normal">· {lastUpdated.toLocaleTimeString()}</span>
              )}
            </Badge>
          </div>
          <div className="h-56 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.sparkline}>
                <XAxis dataKey="day" hide/>
                <Tooltip cursor={{stroke:"var(--rammy-border)"}} contentStyle={{borderRadius:16, border:"1px solid var(--rammy-border)"}}/>
                <Line type="monotone" dataKey="count" stroke="#178677" strokeWidth={2.5} dot={{ r: 3, fill: "#178677" }} activeDot={{r:5}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-6 rammy-shadow relative overflow-hidden">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-[var(--rammy-cyan)]/25 blur-2xl"/>
          <div className="relative">
            <div className="text-xs uppercase tracking-widest text-white/60">Success rate · month</div>
            <div className="text-5xl font-display mt-2" data-testid="admin-flux-success-rate">{successRate}%</div>
            <div className="text-white/70 text-sm mt-1">{t.ok} of {t.total} attempts</div>
            <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/60 space-y-1">
              <div className="flex justify-between"><span>Last 30 days</span><span className="text-white">{data.last_30d.total}</span></div>
              <div className="flex justify-between"><span>All-time spend</span><span className="text-white">${data.all_time.est_cost_usd.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {/* Per-user breakdown — the billing surface */}
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 rammy-shadow">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-medium">Usage by user · this month</div>
            <div className="text-xs text-[var(--rammy-text-muted)]">Aggregated per email — use this to attribute or bill downstream.</div>
          </div>
          <Badge className="rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] border-0">
            {data.by_user.length} {data.by_user.length === 1 ? "user" : "users"}
          </Badge>
        </div>
        {data.by_user.length === 0 ? (
          <div className="text-sm text-[var(--rammy-text-muted)]" data-testid="admin-flux-by-user-empty">No Flux usage from any user this month.</div>
        ) : (
          <Table headers={["User","Successful","Failed","Total","Est. cost (USD)","Last activity"]}>
            {data.by_user.map((r, i) => (
              <tr key={r.email + i} className="border-t border-[var(--rammy-border)]" data-testid={`admin-flux-by-user-row-${i}`}>
                <td className="py-2 pr-3 text-sm">{r.email}</td>
                <td className="py-2 pr-3 text-sm">{r.ok}</td>
                <td className="py-2 pr-3 text-sm text-red-600">{r.fail}</td>
                <td className="py-2 pr-3 text-sm font-medium">{r.total}</td>
                <td className="py-2 pr-3 text-sm font-mono">${r.est_cost_usd.toFixed(3)}</td>
                <td className="py-2 pr-3 text-xs text-[var(--rammy-text-muted)] whitespace-nowrap">{r.last_at ? new Date(r.last_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>

      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 rammy-shadow">
        <div className="font-medium mb-3">Recent fallback events</div>
        {data.recent.length === 0 ? (
          <div className="text-sm text-[var(--rammy-text-muted)]">No Flux fallbacks yet — OpenAI has been handling every request.</div>
        ) : (
          <Table headers={["When","User","Reason","Result","Prompt","Error"]}>
            {data.recent.map((r, i) => (
              <tr key={i} className="border-t border-[var(--rammy-border)]">
                <td className="py-2 pr-3 text-xs text-[var(--rammy-text-muted)] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-2 pr-3 text-sm">{r.email || "—"}</td>
                <td className="py-2 pr-3 text-xs"><Badge className="rounded-full bg-[var(--rammy-cyan)]/15 text-[var(--rammy-cyan-dark)] border-0">{r.reason}</Badge></td>
                <td className="py-2 pr-3 text-sm">
                  {r.ok
                    ? <span className="text-emerald-600 inline-flex items-center gap-1"><Check size={13}/> ok</span>
                    : <span className="text-red-600 inline-flex items-center gap-1"><AlertCircle size={13}/> fail</span>}
                </td>
                <td className="py-2 pr-3 text-xs text-[var(--rammy-text-muted)] max-w-[280px] truncate" title={r.prompt}>{r.prompt}</td>
                <td className="py-2 pr-3 text-xs text-red-600 max-w-[240px] truncate" title={r.error}>{r.error || ""}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function UsersView() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user: me } = useAuth();

  const load = () => api.get("/admin/users").then(r=>setRows(r.data));
  useEffect(() => { load(); }, []);

  const promote = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/promote`);
      toast.success(`${u.name} is now an admin`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not promote"); }
    finally { setBusy(null); }
  };
  const demote = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`/admin/users/${u.id}/demote`);
      toast.success(`${u.name} is no longer an admin`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not demote"); }
    finally { setBusy(null); }
  };

  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-users">
      <Table headers={["Name","Email","Plan","Role","Joined","Actions"]}>
        {rows.map(u => (
          <tr key={u.id} className="border-t border-[var(--rammy-border)] hover:bg-[var(--rammy-surface-alt)]/50">
            <td className="p-3">{u.name}</td>
            <td className="p-3 font-mono text-xs">{u.email}</td>
            <td className="p-3"><Badge className="rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border-0 capitalize">{u.plan}</Badge></td>
            <td className="p-3">
              {u.role === "admin" ? (
                <Badge className="rounded-full bg-[var(--rm-ink-900)] text-[var(--rammy-cyan)] border-0" data-testid={`user-role-${u.id}`}>Admin</Badge>
              ) : (
                <Badge className="rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)] border-0" data-testid={`user-role-${u.id}`}>User</Badge>
              )}
            </td>
            <td className="p-3 text-xs text-[var(--rammy-text-muted)]">{fmtDate(u.created_at)}</td>
            <td className="p-3">
              {u.id === me?.id ? (
                <span className="text-xs text-[var(--rammy-text-muted)]">You</span>
              ) : u.role === "admin" ? (
                <Button onClick={()=>demote(u)} disabled={busy===u.id} variant="ghost"
                  className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-border)] hover:bg-red-50 hover:text-red-700"
                  data-testid={`user-demote-${u.id}`}>
                  Revoke admin
                </Button>
              ) : (
                <Button onClick={()=>promote(u)} disabled={busy===u.id} className="btn-pill h-8 px-3 text-xs btn-dark"
                  data-testid={`user-promote-${u.id}`}>
                  {busy===u.id ? "…" : "Make admin"}
                </Button>
              )}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function SigninsView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/signins?limit=300")
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const norm = q.trim().toLowerCase();
  const filtered = norm
    ? rows.filter(e =>
        (e.email || "").toLowerCase().includes(norm)
        || (e.owner_name || "").toLowerCase().includes(norm)
        || (e.country || "").toLowerCase().includes(norm)
        || (e.city || "").toLowerCase().includes(norm)
        || (e.ip || "").includes(norm))
    : rows;

  // Group most recent per user for the "last 3 sign-ins per user" glance.
  const perUser = {};
  for (const e of filtered) {
    const k = e.user_id;
    if (!perUser[k]) perUser[k] = [];
    if (perUser[k].length < 3) perUser[k].push(e);
  }

  const fmtLoc = (e) => {
    const parts = [];
    if (e.city) parts.push(e.city);
    if (e.region) parts.push(e.region);
    if (e.country) parts.push(e.country);
    return parts.join(", ") || "—";
  };
  const fmtDateTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-signins">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]"/>
          <Input
            data-testid="admin-signins-search"
            placeholder="Filter by email, name, city, country, IP…"
            value={q}
            onChange={e=>setQ(e.target.value)}
            className="pl-9 rounded-full h-10 bg-[var(--rm-surface)]"
          />
        </div>
        <Button onClick={load} variant="ghost" className="btn-pill h-10 px-4 text-sm border border-[var(--rammy-border)]" data-testid="admin-signins-refresh">
          {loading ? "Loading…" : "Refresh"}
        </Button>
        <div className="text-xs text-[var(--rammy-text-muted)] ml-auto">
          {rows.length} events · {Object.keys(perUser).length} users
        </div>
      </div>

      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden">
        <Table headers={["User","When","Location","Timezone","IP","Method","Device"]}>
          {filtered.map(e => (
            <tr key={e.id} className="border-t border-[var(--rammy-border)] hover:bg-[var(--rammy-surface-alt)]/50" data-testid={`signin-row-${e.id}`}>
              <td className="p-3">
                <div className="font-medium text-sm">{e.owner_name || "—"}</div>
                <div className="font-mono text-xs text-[var(--rammy-text-muted)]">{e.email}</div>
              </td>
              <td className="p-3 text-sm">{fmtDateTime(e.created_at)}</td>
              <td className="p-3 text-sm">
                <div className="flex items-center gap-1.5">
                  <MapPin size={12} className="text-[var(--rammy-text-muted)]"/>
                  <span>{fmtLoc(e)}</span>
                </div>
              </td>
              <td className="p-3 text-xs text-[var(--rammy-text-muted)] font-mono">{e.timezone || "—"}</td>
              <td className="p-3 text-xs font-mono">{e.ip || "—"}</td>
              <td className="p-3">
                <Badge className="rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border-0 text-xs">
                  {e.method || "password"}
                </Badge>
              </td>
              <td className="p-3 text-xs text-[var(--rammy-text-muted)] truncate max-w-[280px]" title={e.user_agent}>
                {(e.user_agent || "").split(/\s/)[0] || "—"}
              </td>
            </tr>
          ))}
          {!filtered.length && !loading && (
            <tr><td colSpan={7} className="p-8 text-center text-sm text-[var(--rammy-text-muted)]">No sign-in events yet.</td></tr>
          )}
        </Table>
      </div>
    </div>
  );
}

function InvitesView() {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = () => api.get("/admin/invites").then(r=>setRows(r.data));
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/invites", { email: email.trim() });
      toast.success(`Invite created for ${email}`);
      setEmail("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not invite"); }
    finally { setBusy(false); }
  };
  const revoke = async (inv) => {
    if (!window.confirm(`Revoke invite for ${inv.email}?`)) return;
    try { await api.delete(`/admin/invites/${inv.id}`); load(); toast.success("Invite revoked"); }
    catch { toast.error("Could not revoke"); }
  };
  const copyLink = async (inv) => {
    const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(inv.id); setTimeout(()=>setCopied(null), 1800);
      toast.success("Invite link copied");
    } catch { toast.error("Copy failed — link: " + url); }
  };

  return (
    <div className="space-y-6" data-testid="admin-invites">
      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6">
        <div className="font-medium">Invite a co-admin</div>
        <div className="text-sm text-[var(--rammy-text-sec)] mt-1">
          Send someone a one-time link. They set a password, and land as an admin instantly.
        </div>
        <form onSubmit={send} className="mt-4 flex gap-2 max-w-lg" data-testid="invite-form">
          <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="teammate@company.com"
            className="rounded-full h-11 flex-1" required data-testid="invite-email"/>
          <Button type="submit" disabled={busy} className="btn-pill btn-primary h-11 px-5" data-testid="invite-send">
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </form>
      </div>

      <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden">
        <div className="p-4 border-b border-[var(--rammy-border)] flex items-center justify-between">
          <div className="font-medium">Pending invites</div>
          <div className="text-xs text-[var(--rammy-text-muted)]">{rows.length} open · expire after 7 days</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--rammy-text-muted)]">
            No pending invites. Send one above to bring on a co-admin.
          </div>
        ) : (
          <Table headers={["Email","Invited by","Sent","Expires","Actions"]}>
            {rows.map(inv => (
              <tr key={inv.id} className="border-t border-[var(--rammy-border)]" data-testid={`invite-row-${inv.id}`}>
                <td className="p-3 font-mono text-xs">{inv.email}</td>
                <td className="p-3 text-xs">{inv.invited_by_email}</td>
                <td className="p-3 text-xs text-[var(--rammy-text-muted)]">{fmtDate(inv.created_at)}</td>
                <td className="p-3">
                  {inv.expired
                    ? <Badge className="rounded-full bg-red-100 text-red-700 border-0">Expired</Badge>
                    : <span className="text-xs">{fmtDate(inv.expires_at)}</span>}
                </td>
                <td className="p-3">
                  <div className="flex gap-1.5">
                    <Button onClick={()=>copyLink(inv)} variant="ghost"
                      className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-border)]"
                      data-testid={`invite-copy-${inv.id}`}>
                      <Copy size={12} className="mr-1"/>{copied===inv.id ? "Copied" : "Copy link"}
                    </Button>
                    <Button onClick={()=>revoke(inv)} variant="ghost"
                      className="btn-pill h-8 px-3 text-xs border border-[var(--rammy-border)] hover:bg-red-50 hover:text-red-700"
                      data-testid={`invite-revoke-${inv.id}`}>
                      <Trash2 size={12}/>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function AuditView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then(r=>setRows(r.data)); }, []);
  const actionCls = (a) => a.startsWith("invite") ? "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]"
    : a === "promote" ? "bg-emerald-100 text-emerald-800"
    : a === "demote" ? "bg-amber-100 text-amber-800"
    : "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)]";
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-audit">
      <Table headers={["Actor","Action","Target","When"]}>
        {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-sm text-[var(--rammy-text-muted)]">No admin actions yet. Invite someone or promote a user to see the trail.</td></tr>}
        {rows.map(r => (
          <tr key={r.id} className="border-t border-[var(--rammy-border)]">
            <td className="p-3 font-mono text-xs">{r.actor_email}</td>
            <td className="p-3"><Badge className={`rounded-full border-0 capitalize ${actionCls(r.action)}`}>{r.action.replace("_"," ")}</Badge></td>
            <td className="p-3 font-mono text-xs">{r.target}</td>
            <td className="p-3 text-xs text-[var(--rammy-text-muted)]">{fmtDate(r.created_at)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

function DevicesView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/devices").then(r=>setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-devices">
      <Table headers={["Name","Online","Battery","WiFi","Firmware","Mic"]}>
        {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-[var(--rammy-text-muted)]">No devices bound yet.</td></tr>}
        {rows.map(d => (
          <tr key={d.id} className="border-t border-[var(--rammy-border)]">
            <td className="p-3 font-medium">{d.name}</td>
            <td className="p-3"><span className="inline-flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${d.online?"bg-emerald-500":"bg-neutral-400"}`}/>{d.online?"Online":"Offline"}</span></td>
            <td className="p-3">{d.battery}%</td>
            <td className="p-3">{d.wifi}</td>
            <td className="p-3 font-mono text-xs">{d.firmware}</td>
            <td className="p-3">{d.mic_muted ? "Muted" : "Live"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
function MemoryView() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/memories").then(r=>setRows(r.data)); }, []);
  const filtered = rows.filter(m => !q || `${m.title} ${m.detail} ${m.category} ${m.owner_name} ${m.owner_email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4" data-testid="admin-memory">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[var(--rammy-text-sec)]">
          {rows.length} memories across the fleet · what Rammy remembers about each user.
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rammy-text-muted)]"/>
          <Input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search memories…" className="pl-9 rounded-full h-10 w-72 bg-[var(--rm-surface)]" data-testid="admin-memory-search"/>
        </div>
      </div>
      {rows.length === 0 && (
        <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-8 text-center">
          <Brain size={28} className="mx-auto text-[var(--rammy-text-muted)]"/>
          <div className="mt-3 font-medium">No memories yet</div>
          <div className="text-sm text-[var(--rammy-text-sec)] mt-1 max-w-md mx-auto">
            Memories are the long-term things Rammy remembers about each user — birthdays, preferences, goals, projects. They appear here as soon as any user adds one from the app.
          </div>
          <Link to="/app"><Button className="btn-pill btn-dark mt-4">Add a memory in the app</Button></Link>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {filtered.map(m => (
          <div key={m.id} className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5 flex flex-col">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">{m.category}</div>
              <div className="text-[10px] font-mono text-[var(--rammy-text-muted)]">{m.id.slice(0,8)}</div>
            </div>
            <div className="font-medium mt-1">{m.title}</div>
            {m.detail && <div className="text-sm text-[var(--rammy-text-sec)] mt-1">{m.detail}</div>}
            <div className="mt-auto pt-4 flex items-center gap-2 text-xs text-[var(--rammy-text-muted)] border-t border-[var(--rammy-border)] mt-3">
              <div className="w-6 h-6 rounded-full bg-[var(--rammy-cyan)]/20 flex items-center justify-center text-[var(--rammy-cyan-dark)] font-medium text-[10px]">
                {(m.owner_name || "?")[0]}
              </div>
              <div className="flex-1 truncate">
                <div className="text-[var(--rammy-text)] font-medium truncate">{m.owner_name}</div>
                <div className="truncate">{m.owner_email}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function RemindersView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/reminders").then(r=>setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-reminders">
      <Table headers={["Owner","Title","When","Repeat","Status"]}>
        {rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-sm text-[var(--rammy-text-muted)]">No reminders yet — users can add them from the app.</td></tr>}
        {rows.map(r => (
          <tr key={r.id} className="border-t border-[var(--rammy-border)]">
            <td className="p-3">
              <div className="font-medium">{r.owner_name}</div>
              <div className="text-xs text-[var(--rammy-text-muted)]">{r.owner_email}</div>
            </td>
            <td className="p-3">{r.title}</td>
            <td className="p-3 text-xs font-mono">{fmtDate(r.when)}</td>
            <td className="p-3 capitalize">{r.repeat}</td>
            <td className="p-3">{r.done ? <Badge className="rounded-full bg-emerald-100 text-emerald-800 border-0">Done</Badge> : <Badge className="rounded-full bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)] border-0">Active</Badge>}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
function ConversationsView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/conversations").then(r=>setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-conversations">
      <Table headers={["Owner","Title","Updated","ID"]}>
        {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-sm text-[var(--rammy-text-muted)]">No conversations yet — open the app and say hi to Rammy.</td></tr>}
        {rows.map(c => (
          <tr key={c.id} className="border-t border-[var(--rammy-border)]">
            <td className="p-3">
              <div className="font-medium">{c.owner_name}</div>
              <div className="text-xs text-[var(--rammy-text-muted)]">{c.owner_email}</div>
            </td>
            <td className="p-3">{c.title}</td>
            <td className="p-3 text-xs font-mono">{fmtDate(c.updated_at)}</td>
            <td className="p-3 text-xs font-mono text-[var(--rammy-text-muted)]">{c.id.slice(0,8)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
function AiConfigView() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(null); // provider-model key being saved

  const load = () => api.get("/admin/ai-config").then(r => setCfg(r.data));
  useEffect(() => { load(); }, []);

  const pick = async (p) => {
    const key = `${p.provider}-${p.model}`;
    setSaving(key);
    try {
      const { data } = await api.post("/admin/ai-config", { provider: p.provider, model: p.model });
      setCfg(c => ({ ...c, active: data.active }));
      toast.success(`Rammy is now thinking with ${p.label}`);
    } catch (e) {
      toast.error("Could not switch model");
    } finally { setSaving(null); }
  };

  if (!cfg) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading…</div>;

  const activeLabel = (() => {
    const a = cfg.available.find(m => m.provider === cfg.active.provider && m.model === cfg.active.model);
    return a ? `${a.name} · ${a.label}` : `${cfg.active.provider} · ${cfg.active.model}`;
  })();

  // Group by provider preserving order
  const groups = [];
  const seen = new Set();
  for (const m of cfg.available) {
    if (!seen.has(m.provider)) { seen.add(m.provider); groups.push({ provider: m.provider, name: m.name, items: [] }); }
    groups.find(g => g.provider === m.provider).items.push(m);
  }

  const tierClass = (t) => ({
    frontier:    "bg-[var(--rm-ink-900)] text-[var(--rammy-cyan)]",
    recommended: "bg-[var(--rammy-cyan)] text-[var(--rammy-text)]",
    reasoning:   "bg-[#F7A072] text-[var(--rammy-text)]",
    fast:        "bg-[#79C2E8] text-[var(--rammy-text)]",
    balanced:    "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)]",
  }[t] || "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)]");

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="admin-ai">
      <div className="md:col-span-2 rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--rammy-text-muted)]">Active model</div>
          <div className="text-2xl font-display mt-1" data-testid="admin-ai-active">{activeLabel}</div>
          <div className="text-xs text-[var(--rammy-text-muted)] mt-1">Click a card to switch the model powering every chat, memory extraction & morning briefing.</div>
        </div>

        {groups.map(g => (
          <div key={g.provider}>
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold mb-2 flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${g.provider === "openai" ? "bg-emerald-500" : g.provider === "anthropic" ? "bg-amber-500" : "bg-sky-500"}`}/>
              {g.name}
              <span className="text-[var(--rammy-text-muted)] font-mono normal-case tracking-normal">{g.items.length} models</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {g.items.map(p => {
                const key = `${p.provider}-${p.model}`;
                const active = p.provider === cfg.active.provider && p.model === cfg.active.model;
                const busy = saving === key;
                return (
                  <button
                    key={key}
                    onClick={() => !active && !busy && pick(p)}
                    disabled={active || busy}
                    data-testid={`admin-ai-card-${p.provider}-${p.model}`}
                    data-active={active ? "true" : "false"}
                    aria-pressed={active}
                    className={`text-left rounded-2xl p-4 border transition-all ${
                      active
                        ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10 cursor-default"
                        : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)] hover:-translate-y-0.5 cursor-pointer"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{p.label}</div>
                        <div className="text-xs font-mono text-[var(--rammy-text-muted)] truncate">{p.model}</div>
                      </div>
                      {active && (
                        <Badge className="rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] border-0 text-[10px] shrink-0"
                          data-testid={`admin-ai-active-badge-${p.provider}-${p.model}`}>Active</Badge>
                      )}
                      {busy && <span className="text-[10px] text-[var(--rammy-text-muted)]">Saving…</span>}
                    </div>
                    {p.tier && (
                      <div className="mt-3">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold ${tierClass(p.tier)}`}>
                          {p.tier}
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-[var(--rammy-text-sec)] mt-3 leading-relaxed">{p.note}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="text-xs text-[var(--rammy-text-muted)]">Modular provider layer — swap models without changing the app.</div>
      </div>
      <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-6 rammy-shadow">
        <div className="text-sm text-white/70">System prompt (baseline)</div>
        <div className="mt-3 text-sm leading-relaxed text-white/90 font-mono">
          You are Rammy — a warm, emotionally intelligent AI companion. Speak concisely, humanly, and never call yourself a chatbot.
        </div>
        <div className="mt-6 text-xs text-white/50">
          Applies to chat, memory extraction & morning briefings across the fleet.
        </div>
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Tier legend</div>
          <ul className="mt-3 space-y-1.5 text-xs text-white/80">
            <li><span className="inline-block w-2 h-2 rounded-full bg-[var(--rammy-cyan)] mr-2 align-middle"/>Recommended — everyday choice</li>
            <li><span className="inline-block w-2 h-2 rounded-full bg-[var(--rm-surface)] mr-2 align-middle"/>Frontier — deepest thinking</li>
            <li><span className="inline-block w-2 h-2 rounded-full bg-[#F7A072] mr-2 align-middle"/>Reasoning — planning & logic</li>
            <li><span className="inline-block w-2 h-2 rounded-full bg-[#79C2E8] mr-2 align-middle"/>Fast — quick and cheap</li>
            <li><span className="inline-block w-2 h-2 rounded-full bg-[var(--rammy-surface-alt)] mr-2 align-middle"/>Balanced — good defaults</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
function HomepageVoiceView() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(null);
  const [previewing, setPreviewing] = useState(null); // key of the voice currently playing
  const audioRef = useRef(null);

  const load = () => api.get("/admin/homepage-voice").then(r => setCfg(r.data));
  useEffect(() => { load(); }, []);

  // Stop any playback when the component unmounts.
  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* no-op */ } audioRef.current = null; }
  }, []);

  const preview = (key) => {
    // Toggle: if already playing this voice, pause it.
    if (previewing === key && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setPreviewing(null);
      return;
    }
    if (audioRef.current) { try { audioRef.current.pause(); } catch (_) { /* no-op */ } }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/voice/preview?voice=${encodeURIComponent(key)}&t=${Date.now()}`;
    const el = new Audio(url);
    el.onended = () => setPreviewing(null);
    el.onpause = () => setPreviewing(p => (p === key ? null : p));
    el.onerror = () => { setPreviewing(null); toast.error("Preview didn't play"); };
    audioRef.current = el;
    setPreviewing(key);
    el.play().catch(() => { setPreviewing(null); toast.error("Preview blocked by browser"); });
  };

  const pick = async (key) => {
    setSaving(key);
    try {
      const { data } = await api.post("/admin/homepage-voice", { voice: key });
      setCfg(c => ({ ...c, active: data.active }));
      toast.success(`Homepage will now introduce Rammy in ${cfg.available.find(v => v.key === key)?.label}`);
    } catch (_) {
      toast.error("Could not save voice");
    } finally { setSaving(null); }
  };

  if (!cfg) return <div className="text-sm text-[var(--rammy-text-muted)]">Loading…</div>;

  const activeLabel = cfg.available.find(v => v.key === cfg.active)?.label || cfg.active;

  return (
    <div className="grid md:grid-cols-3 gap-6" data-testid="admin-homepage-voice">
      <div className="md:col-span-2 rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--rammy-text-muted)]">Introducing Rammy as</div>
          <div className="text-2xl font-display mt-1" data-testid="admin-homepage-voice-active">{activeLabel}</div>
          <div className="text-xs text-[var(--rammy-text-muted)] mt-1">
            This is the voice new visitors hear on the landing page. Tap Preview to listen, then Choose to make it live.
          </div>
        </div>

        <div className="rounded-xl bg-[var(--rammy-surface-alt)]/50 border border-[var(--rammy-border)] px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Sample line</div>
          <div className="mt-1 italic text-[var(--rammy-text)]">&ldquo;{cfg.sample_text}&rdquo;</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cfg.available.map(v => {
            const active  = v.key === cfg.active;
            const playing = previewing === v.key;
            const busy    = saving === v.key;
            const s       = (cfg.stats && cfg.stats[v.key]) || { plays: 0, signups: 0 };
            const ctr     = s.plays > 0 ? (s.signups / s.plays * 100) : 0;
            return (
              <div
                key={v.key}
                data-testid={`admin-hv-card-${v.key}`}
                data-active={active ? "true" : "false"}
                className={`rounded-2xl p-4 border transition-all ${
                  active
                    ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/10"
                    : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)]/60"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{v.label}</div>
                      <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)]">{v.gender}</span>
                    </div>
                    <div className="text-[11px] text-[var(--rammy-cyan-dark)] font-semibold mt-0.5">{v.vibe}</div>
                  </div>
                  {active && (
                    <Badge className="rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] border-0 text-[10px] shrink-0"
                      data-testid={`admin-hv-active-badge-${v.key}`}><Check size={10} className="inline mr-1"/> Live</Badge>
                  )}
                </div>
                <div className="text-xs text-[var(--rammy-text-sec)] mt-2 leading-relaxed">{v.note}</div>

                {/* Compact A/B analytics row */}
                <div
                  className="mt-2.5 flex items-center gap-3 text-[10.5px] text-[var(--rammy-text-muted)] font-mono"
                  data-testid={`admin-hv-stats-${v.key}`}
                >
                  <span title="Preview plays"><Play size={9} className="inline mr-0.5 -translate-y-[1px]"/>{s.plays.toLocaleString()}</span>
                  <span title="Signups attributed to this voice"><UserPlus size={9} className="inline mr-0.5 -translate-y-[1px]"/>{s.signups.toLocaleString()}</span>
                  <span
                    title="Signup conversion rate (signups ÷ plays)"
                    className={ctr >= 5 ? "text-[var(--rammy-cyan-dark)] font-semibold" : ""}
                  >
                    <TrendingUp size={9} className="inline mr-0.5 -translate-y-[1px]"/>{s.plays > 0 ? `${ctr.toFixed(1)}%` : "—"}
                  </span>
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => preview(v.key)}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] border transition
                      ${playing
                        ? "border-[var(--rammy-cyan)] bg-[var(--rammy-cyan)]/15 text-[var(--rammy-text)]"
                        : "border-[var(--rammy-border)] text-[var(--rammy-text-sec)] hover:border-[var(--rammy-cyan)]/60 hover:text-[var(--rammy-text)]"}`}
                    data-testid={`admin-hv-preview-${v.key}`}
                  >
                    {playing ? <><Pause size={12}/> Playing</> : <><Play size={12}/> Preview</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => !active && !busy && pick(v.key)}
                    disabled={active || busy}
                    className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition
                      ${active
                        ? "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-muted)] cursor-default"
                        : "bg-[var(--rm-ink-900)] text-white hover:bg-[var(--rammy-cyan-dark)]"}
                      disabled:opacity-60`}
                    data-testid={`admin-hv-choose-${v.key}`}
                  >
                    {busy ? "Saving…" : active ? "Current" : "Choose"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-xs text-[var(--rammy-text-muted)]">
          The greeting audio is cached per voice on the server. First preview after a fresh voice takes ~2 sec; subsequent plays are instant.
        </div>
      </div>

      <div className="rounded-2xl bg-[var(--rm-ink-900)] text-white p-6 rammy-shadow">
        <div className="text-sm text-white/70">What this controls</div>
        <div className="mt-3 text-sm leading-relaxed text-white/90">
          The voice pill on the marketing homepage — the tiny &ldquo;hi, I&rsquo;m Rammy&rdquo; greeting that plays under the hero. This is often the first time a visitor hears Rammy speak, so the choice matters.
        </div>
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Not affected</div>
          <ul className="mt-2 space-y-1 text-xs text-white/70">
            <li>· A signed-in user&rsquo;s personal voice in Settings.</li>
            <li>· Voice Mode conversations inside the app.</li>
            <li>· TTS replies inside chat.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function OtaView() {
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-6" data-testid="admin-ota">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-display">Firmware pipeline</div>
          <div className="text-sm text-[var(--rammy-text-muted)]">Current stable: 1.0.4 · Rollout: 1.0.5 (34% of fleet)</div>
        </div>
        <Button className="btn-pill btn-primary" data-testid="admin-ota-push">Push 1.0.5</Button>
      </div>
      <div className="mt-6 grid md:grid-cols-4 gap-4 text-sm">
        {[
          {stage:"Staged", n: 24},
          {stage:"Downloading", n: 8},
          {stage:"Installing", n: 3},
          {stage:"Complete", n: 41},
        ].map(x => (
          <div key={x.stage} className="rounded-2xl border border-[var(--rammy-border)] p-4">
            <div className="text-xs uppercase tracking-widest text-[var(--rammy-text-muted)]">{x.stage}</div>
            <div className="text-3xl font-display mt-1">{x.n}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function ErrorsView() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/errors").then(r=>setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] overflow-hidden" data-testid="admin-errors">
      <Table headers={["Severity","Device","Code","Message","When"]}>
        {rows.map(e => (
          <tr key={e.id} className="border-t border-[var(--rammy-border)]">
            <td className="p-3">{severityBadge(e.severity)}</td>
            <td className="p-3 font-mono text-xs">{e.device}</td>
            <td className="p-3 font-mono text-xs">{e.code}</td>
            <td className="p-3 text-sm">{e.message}</td>
            <td className="p-3 text-xs text-[var(--rammy-text-muted)]">{fmtDate(e.created_at)}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
function SecurityView() {
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="admin-security">
      {[
        {t:"Encryption at rest",v:"AES-256"},
        {t:"Transport",v:"TLS 1.3"},
        {t:"Auth",v:"JWT + httpOnly cookies"},
        {t:"Password hashing",v:"bcrypt · 12 rounds"},
        {t:"Audit logging",v:"Enabled"},
        {t:"PII deletion",v:"Right to be forgotten · 30d SLA"},
      ].map(x => (
        <div key={x.t} className="rounded-2xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5">
          <div className="text-xs uppercase tracking-widest text-[var(--rammy-text-muted)]">{x.t}</div>
          <div className="text-lg font-display mt-1">{x.v}</div>
        </div>
      ))}
    </div>
  );
}

function severityBadge(s) {
  const cls = s === "error" ? "bg-red-100 text-red-700"
            : s === "warn" ? "bg-amber-100 text-amber-800"
            : "bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)]";
  return <Badge className={`rounded-full border-0 capitalize ${cls}`}>{s}</Badge>;
}

function Table({ headers, children }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[var(--rammy-surface-alt)]/60">
        <tr>{headers.map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-widest text-[var(--rammy-text-muted)] font-medium">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined,{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}); }
  catch { return iso; }
}

/* -----------------------------------------------------------------------
 * Email campaigns — welcome sequence editor
 * ---------------------------------------------------------------------- */
function CampaignsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [preview, setPreview] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-campaigns");
      setItems(data || []);
      const map = {};
      (data || []).forEach(c => { map[c.key] = { subject: c.subject, body: c.body, day_offset: c.day_offset, enabled: c.enabled }; });
      setDrafts(map);
    } catch { toast.error("Couldn't load campaigns"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patch = async (key, patchBody) => {
    setSaving(key);
    try {
      await api.patch(`/admin/email-campaigns/${key}`, patchBody);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(null); }
  };

  const showPreview = async (key) => {
    try {
      const { data } = await api.post(`/admin/email-campaigns/${key}/preview`);
      setPreview(data);
    } catch { toast.error("Preview failed"); }
  };

  const setDraft = (key, field, val) => {
    setDrafts(d => ({ ...d, [key]: { ...d[key], [field]: val } }));
  };

  if (loading) return <div className="p-8 text-sm text-[var(--rammy-text-muted)]">Loading…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="campaigns-view">
      <div>
        <div className="text-2xl font-display">Email campaigns</div>
        <p className="text-sm text-[var(--rammy-text-sec)] mt-1 max-w-lg">
          A three-touch welcome arc that goes out via Resend after signup. Edit copy, timing, and switch any step off. Placeholders <code className="text-xs font-mono bg-[var(--rammy-surface-alt)] px-1 rounded">{"{first_name}"}</code> and <code className="text-xs font-mono bg-[var(--rammy-surface-alt)] px-1 rounded">{"{app_link}"}</code> are auto-filled.
        </p>
      </div>

      {items.map(c => {
        const d = drafts[c.key] || {};
        const dirty = d.subject !== c.subject || d.body !== c.body || d.day_offset !== c.day_offset || d.enabled !== c.enabled;
        return (
          <div key={c.key} className="rounded-3xl bg-[var(--rm-surface)] border border-[var(--rammy-border)] p-5" data-testid={`campaign-${c.key}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Sequence step</div>
                <div className="text-lg font-display mt-1">{c.name}</div>
                <div className="text-[11px] font-mono text-[var(--rammy-text-muted)] mt-0.5">
                  key: {c.key} · sent to {c.sent_count} user{c.sent_count === 1 ? "" : "s"}
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-[var(--rammy-text-muted)] shrink-0">
                <input
                  type="checkbox"
                  checked={!!d.enabled}
                  onChange={(e)=>setDraft(c.key, "enabled", e.target.checked)}
                  data-testid={`campaign-${c.key}-enabled`}
                />
                Enabled
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="col-span-2">
                <label className="block text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Subject</label>
                <Input
                  value={d.subject || ""}
                  onChange={(e)=>setDraft(c.key, "subject", e.target.value)}
                  className="mt-1 rounded-xl"
                  data-testid={`campaign-${c.key}-subject`}
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Send day (after signup)</label>
                <Input
                  type="number"
                  min="0"
                  max="90"
                  value={d.day_offset ?? 0}
                  onChange={(e)=>setDraft(c.key, "day_offset", parseInt(e.target.value || "0"))}
                  className="mt-1 rounded-xl"
                  data-testid={`campaign-${c.key}-day`}
                />
              </div>
            </div>

            <label className="block text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold mt-4">Body</label>
            <textarea
              value={d.body || ""}
              onChange={(e)=>setDraft(c.key, "body", e.target.value)}
              rows={9}
              className="mt-1 w-full rounded-xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-3 text-sm leading-relaxed outline-none focus:border-[var(--rammy-cyan)] resize-y font-sans"
              data-testid={`campaign-${c.key}-body`}
            />

            <div className="mt-4 flex justify-end gap-2 flex-wrap">
              <Button
                variant="ghost"
                className="btn-pill border border-[var(--rammy-border)]"
                onClick={()=>showPreview(c.key)}
                data-testid={`campaign-${c.key}-preview`}
              >
                Preview
              </Button>
              <Button
                onClick={()=>patch(c.key, d)}
                disabled={!dirty || saving === c.key}
                className="btn-pill btn-primary"
                data-testid={`campaign-${c.key}-save`}
              >
                {saving === c.key ? "Saving…" : dirty ? "Save changes" : "Saved"}
              </Button>
            </div>
          </div>
        );
      })}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={()=>setPreview(null)} data-testid="campaign-preview-modal">
          <div className="w-full max-w-lg rounded-3xl bg-[var(--rm-surface)] p-6 fade-up" onClick={e=>e.stopPropagation()}>
            <div className="text-[10px] uppercase tracking-widest text-[var(--rammy-text-muted)] font-semibold">Preview · with your first name</div>
            <div className="mt-2 font-display text-lg">{preview.subject}</div>
            <div className="mt-4 rounded-2xl bg-[var(--rammy-surface-alt)] p-4 text-sm leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto">
              {preview.body}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={()=>setPreview(null)} className="btn-pill btn-primary" data-testid="campaign-preview-close">Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

